import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Router, RouterLink} from '@angular/router';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {InputText} from 'primeng/inputtext';
import {Password} from 'primeng/password';
import {Button} from 'primeng/button';
import {TwoFaComponent} from '../two-fa/two-fa.component';
import {LoginService} from '../../core/services/login-service/login.service';
import {AuthService} from '../../core/services/auth-service/auth.service';
import {Message, ResendOTPRequest, User} from '../../interfaces/loginInterface';
import {FooterComponent} from '../../shared/footer/footer.component';
import {LogoService} from '../../core/services/logo-service/logo.service';
import {UserService} from '../../core/services/user-service/user.service';
import {generatePalette} from '../themes/themeService';
import {updatePrimaryPalette} from '@primeng/themes';

@Component({
  selector: 'app-login',
  standalone: true,
  templateUrl: './login.component.html',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    InputText,
    Password,
    Button,
    TwoFaComponent,
    FooterComponent,
  ],
  providers: [LoginService, AuthService],
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  password = '';
  showLoader = false;
  errorMessage: string = '';
  search = '';
  logoUrl: string = "../../../assets/icons/ptm-header-logo.svg";
  showTwoFa = false;
  twoFaData: ResendOTPRequest | null = null

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private loginService: LoginService,
    private authService: AuthService,
    private logoService: LogoService,
    private userService: UserService,
  ) {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')]],
      password: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.logoService.logo$.subscribe((url) => {
      if (url) {
        this.logoUrl = url;
      }
    });
    const tokenString = localStorage.getItem('vendorClientUser_data');
    if (tokenString) {
      try {
        const token = JSON.parse(tokenString);
        if (token.access_token) {
          this.router.navigate(['/dashboard']);
          return;
        }
      } catch (error) {
        console.error('Invalid token on login page.');
      }
    }
    this.loginForm.get('username')?.valueChanges.pipe(debounceTime(100), distinctUntilChanged()).subscribe(() => {
      this.handleEmailChange();
    });
    this.loginForm.get('password')?.valueChanges.subscribe(() => {
      this.handlePasswordChange();
    });
  }

// In your login component
  login(): void {
    if (this.loginForm.invalid) {
      this.errorMessage = "Please enter a valid email and password.";
      return;
    }
    this.showLoader = true;
    this.loginService.login(this.loginForm.value).subscribe({
      next: (data) => {
        this.authService.saveCredentials(data);
        if (data.message === Message.OTP_REQUIRED) {
          this.showTwoFa = true;
          this.twoFaData = data;
        } else {
          this.getLoggedInUserProfile(); // Fetch user profile after login
          this.router.navigate(['/dashboard']);
          this.showLoader = false;
          this.showTwoFa = false;
        }
      },
      error: (error: any) => {
        this.errorMessage = error.error?.reason;
        this.showLoader = false;
      },
    });
  }

  getLoggedInUserProfile(): void {
    this.loginService.getCustomerLoginUser().subscribe({
      next: (res: User) => {
        this.userService.setUser(res);
        if (res.theme?.colour) {
          const color = res.theme.colour;
          localStorage.setItem('user-theme-color', color);
          this.applyTheme(color);
        }
      },
      error: err => {
        console.error('Failed to load user profile', err);
      }
    });
  }

  private applyTheme(color: string): void {
    const palette = generatePalette(color);
    updatePrimaryPalette(palette);
    Object.entries(palette).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--p-primary-${key}`, value);
    });
  }

  getForgotPassword(): string {
    return '/forget-password';
  }

  getRegisterUser(): string {
    return '/register';
  }

  handleEmailChange(): void {
    const usernameControl = this.loginForm.get('username');
    if (usernameControl?.touched || usernameControl?.dirty) {
      if (usernameControl?.invalid) {
        this.showLoader = false;
        if (usernameControl.errors) {
          if (usernameControl.errors['required']) {
            this.errorMessage = 'Email is required.';
          } else if (usernameControl.errors['pattern']) {
            this.errorMessage = 'Please enter a valid email address.';
          }
        }
      } else {
        this.errorMessage = '';
      }
    }
  }

  handlePasswordChange(): void {
    const passwordControl = this.loginForm.get('password');
    if (passwordControl?.touched || passwordControl?.dirty) {
      if (passwordControl?.invalid) {
        this.showLoader = false;
        if (passwordControl.errors) {
          if (passwordControl.errors['required']) {
            this.errorMessage = 'Password cannot be empty.';
          }
        }
      } else {
        this.errorMessage = '';
      }
    }
  }

}
